import { CheckIcon, LockIcon } from 'lucide-react'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import {
  BEHAVIORAL_SPECS,
  type Talent,
  type TalentSpec,
  tierLocked,
  topicStatus,
} from '../data/mastery-data'

function TalentNode({ talent, locked }: { talent: Talent; locked: boolean }) {
  const done = talent.subtopics.filter((st) => st.mastered).length
  const total = talent.subtopics.length
  const status = topicStatus(talent.subtopics, !locked)

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label={`${talent.name}: ${done} of ${total} mastered${locked ? ' (locked)' : ''}`}
          className="flex w-20 flex-col items-center gap-1.5 focus-visible:outline-none [&:focus-visible>span:first-child]:ring-2 [&:focus-visible>span:first-child]:ring-ring/60"
        >
          <span
            className={cn(
              'flex size-12 items-center justify-center rounded-xl border-2 text-sm font-bold transition-all',
              status === 'mastered' &&
                'border-primary bg-primary text-primary-foreground shadow-[0_0_14px_-2px] shadow-primary/50',
              (status === 'reinforcing' || status === 'learning') &&
                'border-primary bg-primary/15 text-primary',
              status === 'available' &&
                'border-task-exercise bg-task-exercise/10 text-task-exercise hover:border-task-exercise',
              status === 'locked' &&
                'border-border/50 bg-muted/40 text-muted-foreground/40',
            )}
            aria-hidden="true"
          >
            {status === 'mastered' ? (
              <CheckIcon className="size-5" />
            ) : status === 'locked' ? (
              <LockIcon className="size-4" />
            ) : (
              talent.code
            )}
          </span>
          <span
            className={cn(
              'text-center text-[11px] leading-tight font-medium text-balance',
              locked ? 'text-muted-foreground/50' : 'text-foreground/80',
            )}
          >
            {talent.name}
          </span>
          <span
            className={cn(
              'text-[10px] font-semibold tabular-nums',
              status === 'mastered' ? 'text-primary' : 'text-muted-foreground',
            )}
          >
            {done}/{total}
          </span>
        </button>
      </TooltipTrigger>
      <TooltipContent className="max-w-56">
        <div className="flex flex-col gap-1.5 py-0.5">
          <span className="font-semibold">{talent.name}</span>
          <ul className="flex flex-col gap-1">
            {talent.subtopics.map((st) => (
              <li key={st.name} className="flex items-center gap-1.5 text-xs">
                <span
                  className={cn(
                    'size-1.5 shrink-0 rounded-full',
                    st.mastered
                      ? 'bg-ready-green-light'
                      : 'bg-muted-foreground/40',
                  )}
                  aria-hidden="true"
                />
                {st.name}
              </li>
            ))}
          </ul>
        </div>
      </TooltipContent>
    </Tooltip>
  )
}

function SpecColumn({ spec }: { spec: TalentSpec }) {
  return (
    <div className="flex min-w-56 flex-1 flex-col overflow-hidden rounded-lg border bg-card">
      <div className="border-b bg-muted/50 px-4 py-2.5">
        <h4 className="text-sm font-semibold">{spec.name}</h4>
      </div>
      <div className="flex flex-col">
        {spec.tiers.map((tier) => {
          const locked = tierLocked(spec, tier.tier)
          return (
            <div
              key={tier.tier}
              className={cn(
                'flex flex-col gap-3 border-b p-4 last:border-b-0',
                locked && 'bg-muted/20',
              )}
            >
              <span className="flex items-center gap-1.5 text-[10px] font-semibold tracking-wider text-muted-foreground uppercase">
                Tier {tier.tier}
                {locked && (
                  <>
                    <LockIcon className="size-3" aria-hidden="true" />
                    <span className="normal-case">
                      requires all tier {tier.tier - 1} skills
                    </span>
                  </>
                )}
              </span>
              <div className="flex flex-wrap gap-3">
                {tier.talents.map((talent) => (
                  <TalentNode key={talent.id} talent={talent} locked={locked} />
                ))}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export function TalentTree() {
  return (
    <div className="flex flex-col gap-4 xl:flex-row">
      {BEHAVIORAL_SPECS.map((spec) => (
        <SpecColumn key={spec.id} spec={spec} />
      ))}
    </div>
  )
}
